import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { BreadcrumbItem, BreadcrumbPage } from '~/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { useLastDepartment } from '~/hooks/use-last-department';
import { meQueryOptions } from '~/lib/auth';
import { departmentsQueryOptions } from '~/lib/departments';

interface DepartmentBreadcrumbProps {
  orgSlug: string;
  deptSlug: string;
}

export function DepartmentBreadcrumb({
  orgSlug,
  deptSlug
}: DepartmentBreadcrumbProps): React.ReactNode {
  const navigate = useNavigate();
  const { data } = useQuery(departmentsQueryOptions);
  const { data: session } = useQuery(meQueryOptions);
  const [, setLastDepartment] = useLastDepartment();
  const [open, setOpen] = useState(false);

  const departments = data?.departments ?? [];
  const activeDept = departments.find((d) => d.slug === deptSlug);

  if (departments.length <= 1) {
    return (
      <BreadcrumbItem>
        <BreadcrumbPage>{activeDept?.name ?? deptSlug}</BreadcrumbPage>
      </BreadcrumbItem>
    );
  }

  return (
    <BreadcrumbItem>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger className="flex items-center gap-1 text-foreground hover:text-foreground/80 transition-colors">
          <span className="font-normal">{activeDept?.name ?? deptSlug}</span>
          <ChevronDown className="size-3.5 text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={deptSlug}
            onValueChange={(value: string) => {
              const dept = departments.find((d) => d.slug === value);
              if (dept && session) {
                setOpen(false);
                setLastDepartment(value);
                void navigate({
                  to: '/org/$orgSlug/department/$deptSlug/jobs',
                  params: { orgSlug, deptSlug: value }
                });
              }
            }}
          >
            {departments.map((dept) => (
              <DropdownMenuRadioItem key={dept.id} value={dept.slug}>
                {dept.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </BreadcrumbItem>
  );
}
